import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Radio, BarChart3, History as HistoryIcon, Users, CalendarClock } from "lucide-react";
import PresenceIndicator from "@/components/PresenceIndicator";
import OnlineDriversBadge from "@/components/OnlineDriversBadge";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const navItems = [];
  if (user?.role === "admin") {
    navItems.push({ to: "/admin", label: "Admin", icon: LayoutDashboard });
    navItems.push({ to: "/dispatch", label: "Dispatch", icon: Radio });
    navItems.push({ to: "/reports", label: "Reportes", icon: BarChart3 });
    navItems.push({ to: "/history", label: "Historial", icon: HistoryIcon });
    navItems.push({ to: "/clients", label: "Clientes", icon: Users });
    navItems.push({ to: "/appointments", label: "Citas", icon: CalendarClock });
  } else if (user?.role === "dispatcher") {
    navItems.push({ to: "/dispatch", label: "Dispatch", icon: Radio });
    navItems.push({ to: "/reports", label: "Reportes", icon: BarChart3 });
    navItems.push({ to: "/history", label: "Historial", icon: HistoryIcon });
    navItems.push({ to: "/clients", label: "Clientes", icon: Users });
    navItems.push({ to: "/appointments", label: "Citas", icon: CalendarClock });
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
              <div className="relative w-11 h-11 rounded-md bg-white/95 grid place-items-center shadow-[0_0_20px_rgba(251,191,36,0.35)] p-1">
                <img src="/logo.webp" alt="Mi Taxi Naples" className="w-full h-full object-contain" />
              </div>
              <div className="leading-tight">
                <div className="font-black tracking-tight text-white" style={{ fontFamily: "Manrope", fontSize: "15px" }}>
                  MI TAXI <span className="text-primary">NAPLES</span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                  Dispatch · FL
                </div>
              </div>
            </Link>
            <nav className="flex gap-1">
              {navItems.map((it) => {
                const active = loc.pathname === it.to;
                const Icon = it.icon;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.label.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-white hover:bg-secondary"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {it.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <OnlineDriversBadge />
            <PresenceIndicator />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{user?.name || user?.email}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest font-mono">{user?.role}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-button"
              className="border-border"
            >
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 flex flex-col">{children}</main>
    </div>
  );
}
